import { useLayoutEffect, useRef, useState } from 'react';
import { Globe as GlobeIcon, AlertCircle } from 'lucide-react';
import { SubscriptionDialog } from '@/components/ui/subscription-dialog';
import { useSubscriptionGuard } from '@/hooks/useSubscriptionGuard';
import { GlobeCanvas } from '@/components/route-map/GlobeCanvas';
import { RouteSidebar } from '@/components/route-map/RouteSidebar';
import { RouteFallbackView } from '@/components/route-map/RouteFallbackView';
import { AirportEditDialog } from '@/components/route-map/AirportEditDialog';
import { useRouteData } from '@/components/route-map/useRouteData';
import { useMediaQuery, useWebGLSupport } from '@/components/route-map/useEnvironment';

export default function FsopRouteMapPage() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [year, setYear] = useState<number | 'all'>('all');
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const { routes, unknownAirports, yearOptions, loading, error, subscriptionRequired, refetch } = useRouteData({ year });
  const { showDialog, setShowDialog } = useSubscriptionGuard();
  const isLg = useMediaQuery('(min-width: 1024px)');
  const hasWebGL = useWebGLSupport();
  const showGlobe = isLg && hasWebGL;

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateSize = () => {
      const rect = el.getBoundingClientRect();
      setSize({ width: Math.floor(rect.width), height: Math.floor(rect.height) });
    };

    updateSize();

    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => updateSize())
        : null;

    if (ro) ro.observe(el);
    return () => ro?.disconnect();
  }, []);

  return (
    <div className="dark relative -m-6 lg:-m-8 min-h-[calc(100vh-4rem)] overflow-hidden bg-[#05060a] text-slate-100">
      <SubscriptionDialog isOpen={subscriptionRequired || showDialog} onClose={() => setShowDialog(false)} />
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,#0b1530_0%,#000_70%)]"
      />

      <header className="relative z-10 flex items-center gap-3 px-6 lg:px-8 pt-6 pb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-violet-600 flex items-center justify-center shadow-[0_0_24px_rgba(56,189,248,0.4)]">
          <GlobeIcon className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-wide bg-gradient-to-r from-cyan-300 via-sky-300 to-violet-400 bg-clip-text text-transparent">
            航线地图
          </h1>
          <p className="text-xs text-slate-400">您飞过的每一条航线,都在这里发光</p>
        </div>
      </header>

      {error && (
        <div className="relative z-10 flex flex-col items-center justify-center gap-3 py-24">
          <AlertCircle className="w-8 h-8 text-rose-400" />
          <p className="text-sm text-rose-300">{error}</p>
        </div>
      )}

      {!error && showGlobe && (
        <div className="relative z-0 flex h-[calc(100vh-8rem)]">
          <div ref={containerRef} className="flex-1 relative">
            {size.width > 0 && size.height > 0 && !loading && (
              <GlobeCanvas routes={routes} width={size.width} height={size.height} />
            )}
            {loading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 z-10">
                <div className="w-12 h-12 rounded-full border-2 border-cyan-400/30 border-t-cyan-400 animate-spin" />
                <p className="text-sm text-slate-400">正在汇聚航线数据…</p>
              </div>
            )}
          </div>
          <RouteSidebar
            routes={routes}
            unknownAirports={unknownAirports}
            year={year}
            yearOptions={yearOptions}
            onYearChange={setYear}
            onEditAirport={setEditingCode}
          />
        </div>
      )}

      {!error && !showGlobe && (
        <>
          {loading && (
            <div className="relative z-10 flex flex-col items-center justify-center gap-3 py-24">
              <div className="w-12 h-12 rounded-full border-2 border-cyan-400/30 border-t-cyan-400 animate-spin" />
              <p className="text-sm text-slate-400">正在汇聚航线数据…</p>
            </div>
          )}
          {!loading && (
            <RouteFallbackView
              routes={routes}
              unknownAirports={unknownAirports}
              year={year}
              yearOptions={yearOptions}
              onYearChange={setYear}
              onEditAirport={setEditingCode}
              webglUnsupported={isLg && !hasWebGL}
            />
          )}
        </>
      )}

      <AirportEditDialog
        code={editingCode}
        onClose={() => setEditingCode(null)}
        onSaved={refetch}
      />
    </div>
  );
}
